import React, { useState } from 'react';
import type { Socket } from 'socket.io-client';
import { AdminWildcardPowerPanel } from './AdminWildcardPowerPanel';

interface AdminPanelCombatTestingTabProps {
  socket?: Socket | null;
  playerId?: string;
  isBusy: boolean;
  onStateChange: (state: { isBusy: boolean; lastMessage: string | null }) => void;
}

// Essence types for spawning
const ESSENCES = [
  { type: 'flame', name: 'Flame', emoji: '🔥' },
  { type: 'storm', name: 'Storm', emoji: '⚡' },
  { type: 'tide', name: 'Tide', emoji: '💧' },
  { type: 'shadow', name: 'Shadow', emoji: '🌑' },
  { type: 'iron', name: 'Iron', emoji: '⚙️' },
  { type: 'spirit', name: 'Spirit', emoji: '👻' },
  { type: 'inferno', name: 'Inferno', emoji: '🔥' },
  { type: 'glacier', name: 'Glacier', emoji: '❄️' },
  { type: 'dragon', name: 'Dragon', emoji: '🐉' },
  { type: 'void_king', name: 'Void King', emoji: '👑' },
] as const;

/**
 * Dedicated admin tab for combat visual testing and signature move development
 * Contains all combat effect testing, signature move testing, and visual debugging tools
 */
export const AdminPanelCombatTestingTab: React.FC<AdminPanelCombatTestingTabProps> = ({
  socket,
  playerId,
  isBusy,
  onStateChange,
}) => {
   const [expandedSection, setExpandedSection] = useState<string | null>('signature-moves');
   const [essenceQuantity, setEssenceQuantity] = useState<string>('1');

   const toggleSection = (section: string): void => {
     setExpandedSection(expandedSection === section ? null : section);
   };

   const spawnEssence = (essenceType: string): void => {
     if (!socket) {
       onStateChange({ isBusy: false, lastMessage: 'Socket not connected' });
       return;
     }

     const quantity = parseInt(essenceQuantity, 10);
     if (isNaN(quantity) || quantity < 1 || quantity > 999) {
       onStateChange({ isBusy: false, lastMessage: 'Quantity must be 1-999' });
       return;
     }

     onStateChange({ isBusy: true, lastMessage: `Spawning essence...` });

     socket.emit('admin:spawnEssence', {
       essenceType,
       quantity,
     }, (response: { success: boolean; message: string }) => {
       onStateChange({
         isBusy: false,
         lastMessage: response.message,
       });
     });
   };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Header */}
      <div style={{ padding: '6px', border: '1px solid #4a5568', backgroundColor: '#111827' }}>
        <div style={{ fontWeight: 600, fontSize: 10, color: '#ffd700' }}>
          ⚔️ Combat Testing & Visual Debugging
        </div>
        <div style={{ fontSize: 8, color: '#a0aec0', marginTop: 2 }}>
          Test combat visuals, signature moves, and weapon effects with real-time feedback
        </div>
      </div>

      {/* Signature Move Testing Section */}
      <div style={{ padding: '6px', border: '1px solid #4a5568', backgroundColor: '#111827' }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: 10,
            marginBottom: 4,
            cursor: 'pointer',
            userSelect: 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: '#00bfff',
          }}
          onClick={() => toggleSection('signature-moves')}
        >
          ✨ Signature Move Testing
          <span style={{ fontSize: 8, color: '#a0aec0' }}>
            {expandedSection === 'signature-moves' ? '▼' : '▶'}
          </span>
        </div>

        {expandedSection === 'signature-moves' && (
          <AdminWildcardPowerPanel
            socket={socket}
            playerId={playerId}
            isBusy={isBusy}
            onStateChange={onStateChange}
          />
       )}
       </div>

       {/* Essence Spawning Section */}
       <div style={{ padding: '6px', border: '1px solid #4a5568', backgroundColor: '#111827' }}>
         <div
           style={{
             fontWeight: 600,
             fontSize: 10,
             marginBottom: 4,
             cursor: 'pointer',
             userSelect: 'none',
             display: 'flex',
             justifyContent: 'space-between',
             alignItems: 'center',
             color: '#9932cc',
           }}
           onClick={() => toggleSection('essence-spawning')}
         >
           💎 Essence Spawning
           <span style={{ fontSize: 8, color: '#a0aec0' }}>
             {expandedSection === 'essence-spawning' ? '▼' : '▶'}
           </span>
         </div>

         {expandedSection === 'essence-spawning' && (
           <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
             <div style={{ fontSize: 8, color: '#a0aec0' }}>
               Spawn essence items into your inventory to test signature move unlocking
             </div>

             {/* Quantity Input */}
             <div>
               <div style={{ fontSize: 8, color: '#a0aec0', marginBottom: 2 }}>Quantity (1-999):</div>
               <input
                 type="number"
                 min="1"
                 max="999"
                 value={essenceQuantity}
                 onChange={(e) => setEssenceQuantity(e.target.value)}
                 disabled={isBusy}
                 style={{
                   width: '100%',
                   padding: '4px',
                   backgroundColor: '#1a202c',
                   border: '1px solid #4a5568',
                   color: '#e2e8f0',
                   fontSize: 8,
                 }}
               />
             </div>

             {/* Essence Buttons Grid */}
             <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
               {ESSENCES.map(({ type, name, emoji }) => (
                 <button
                   key={type}
                   onClick={() => spawnEssence(type)}
                   disabled={isBusy}
                   style={{
                     padding: '6px',
                     backgroundColor: isBusy ? '#2d3748' : '#2c5aa0',
                     border: '1px solid #4a5568',
                     color: '#e2e8f0',
                     fontSize: 8,
                     fontWeight: 600,
                     cursor: isBusy ? 'not-allowed' : 'pointer',
                     opacity: isBusy ? 0.5 : 1,
                   }}
                 >
                   {emoji} {name}
                 </button>
               ))}
             </div>
           </div>
         )}
       </div>

       {/* Combat Features Guide */}
      <div style={{ padding: '6px', border: '1px solid #4a5568', backgroundColor: '#111827' }}>
        <div style={{ fontWeight: 600, fontSize: 10, marginBottom: 4, color: '#ffd700' }}>
          📋 Testing Features Available
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ fontSize: 8, color: '#a0aec0', display: 'flex', gap: 2 }}>
            <span style={{ minWidth: '20px' }}>🎯</span>
            <span><strong>Weapon Tiers:</strong> Test 5 rarity levels with tier visualization</span>
          </div>
          <div style={{ fontSize: 8, color: '#a0aec0', display: 'flex', gap: 2 }}>
            <span style={{ minWidth: '20px' }}>✨</span>
            <span><strong>Elements:</strong> 6 signature elements with unique colors & effects</span>
          </div>
          <div style={{ fontSize: 8, color: '#a0aec0', display: 'flex', gap: 2 }}>
            <span style={{ minWidth: '20px' }}>📊</span>
            <span><strong>Intensity:</strong> Real-time multiplier (0.5x-3.0x) for effect scaling</span>
          </div>
          <div style={{ fontSize: 8, color: '#a0aec0', display: 'flex', gap: 2 }}>
            <span style={{ minWidth: '20px' }}>🎨</span>
            <span><strong>Colors:</strong> 18 color layers (3 per element) visible in preview</span>
          </div>
          <div style={{ fontSize: 8, color: '#a0aec0', display: 'flex', gap: 2 }}>
            <span style={{ minWidth: '20px' }}>💥</span>
            <span><strong>AOE:</strong> Adjustable blast radius (0-10 tiles) for area testing</span>
          </div>
          <div style={{ fontSize: 8, color: '#a0aec0', display: 'flex', gap: 2 }}>
            <span style={{ minWidth: '20px' }}>⚡</span>
            <span><strong>Quick-Equip:</strong> 6 preset moves for rapid combo testing</span>
          </div>
        </div>
      </div>

      {/* Performance Tips */}
      <div style={{ padding: '6px', border: '1px solid #4a5568', backgroundColor: '#111827' }}>
        <div style={{ fontWeight: 600, fontSize: 10, marginBottom: 4, color: '#4caf50' }}>
          💡 Testing Tips
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <div style={{ fontSize: 8, color: '#a0aec0' }}>
            • Start with Legendary tier to see maximum visual intensity
          </div>
          <div style={{ fontSize: 8, color: '#a0aec0' }}>
            • Adjust intensity multiplier to test effect scaling
          </div>
          <div style={{ fontSize: 8, color: '#a0aec0' }}>
            • Use color preview to verify element identification
          </div>
          <div style={{ fontSize: 8, color: '#a0aec0' }}>
            • Test AOE radius to check area effect clarity
          </div>
          <div style={{ fontSize: 8, color: '#a0aec0' }}>
            • Combine high power + high intensity for stress testing
          </div>
        </div>
      </div>
    </div>
  );
};
